/**
 * Check-in Screen — vehicle inspection at pickup.
 *
 * Captures fuel level, odometer, exterior/interior condition, photos (6 angles),
 * and optional notes. Validates with Zod before submitting.
 */
import { useState, useCallback, useEffect, useRef } from 'react'
import { View, Text, TextInput as RNTextInput } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { notification, NotificationType } from '@/lib/utils/haptics'
import { FONTS, SPACING, RADIUS } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { useAuth } from '@/lib/hooks/use-auth'
import { useBooking } from '@/lib/hooks/use-booking'
import { ServiceError } from '@/lib/errors/service-error'
import { useCreateInspection } from '@/lib/hooks/use-inspection'
import { HandoverPresenceCard } from '@/components/trip/HandoverPresenceCard'
import { useCheckinDraftStore } from '@/lib/stores/checkin.store'
import { useVerificationOverview } from '@/lib/hooks/use-verification'
import { inspectionFormSchema } from '@/lib/validations/inspection.schema'
import { etapaDeInspeccion, tituloDeEtapa, cuerpoDeEtapa } from '@/lib/utils/inspection-stage'
import { sanitizeOdometerText } from '@/lib/utils/odometer-input'
import {
  hasRequiredLiveLicenseEvidence,
  requiresLiveLicenseEvidence,
} from '@/lib/utils/inspection-license-evidence'
import {
  canPersistInspectionDraft,
  hydrateInspectionDraft,
} from '@/lib/utils/inspection-draft-hydration'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'
import { RETENTION_EVENTS } from '@/lib/retention/events'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { EmptyState } from '@/components/ui/EmptyState'
import { SignaturePad } from '@/components/ui/SignaturePad'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { FuelLevelSlider } from '@/components/trip/FuelLevelSlider'
import { ConditionPicker } from '@/components/trip/ConditionPicker'
import { InspectionPhotoGrid } from '@/components/trip/InspectionPhotoGrid'
import { InspectionReadinessCard } from '@/components/trip/InspectionReadinessCard'
import {
  INSPECTION_PHOTO_AREAS,
  LICENSE_PHOTO_AREA,
  MIN_INSPECTION_PHOTOS,
  faltanAreasObligatorias,
  type ConditionGrade,
  type InspectionPhoto,
  type InspectionPhotoArea,
} from '@/lib/types/inspection.types'

/**
 * Distinguir "no existe" de "no llego".
 *
 * `getBookingDetail` LANZA en los dos casos: `maybeSingle()` con cero filas
 * devuelve `data: null` sin error, y el servicio convierte eso en
 * `ServiceError.notFound`. O sea que `bookingError` esta puesto tambien cuando
 * la reserva simplemente no existe -- y una rama que solo mire "hay error"
 * manda TODO al muro de transporte, dejando muerto el otro.
 *
 * Me lo encontro una revision de mi propia compuerta: separe tres hechos y el
 * tercero era inalcanzable. El discriminador es el codigo, no la presencia del
 * error.
 */
/**
 * El error de subida, dicho con nombres de fotos.
 *
 * `INSPECTION_PHOTOS_FAILED` viaja con `details.failedAreas`, y hasta hoy eso
 * no llegaba a la pantalla: se mostraba "No se pudo completar la entrega" y la
 * persona no tenia forma de saber CUAL de las dieciseis fotos falto. Con quince
 * angulos identicos en una cuadricula, eso es imposible de adivinar.
 *
 * Y ahora se puede prometer sin mentir que reintentar es barato: la subida es
 * idempotente por area y las que ya estan arriba no se vuelven a subir.
 */
function mensajeDeFotosFallidas(
  err: unknown,
  t: (clave: string, opciones?: Record<string, unknown>) => string,
): string | null {
  if (!(err instanceof ServiceError) || err.code !== 'INSPECTION_PHOTOS_FAILED') return null
  const detalles = err.details as { failedAreas?: unknown } | undefined
  const areas = Array.isArray(detalles?.failedAreas)
    ? detalles.failedAreas.filter((a): a is string => typeof a === 'string')
    : []
  if (areas.length === 0) return null
  return t('checkin.photos_failed_body', {
    areas: areas.map((area) => t(`checkin.photo_${area}`)).join(', '),
  })
}

function esReservaInexistente(err: unknown): boolean {
  return err instanceof ServiceError && err.code === 'NOT_FOUND'
}

export default function CheckinScreen() {
  const { bookingId } = useLocalSearchParams<{ bookingId: string }>()
  const {
    data: booking,
    isLoading: bookingLoading,
    error: bookingError,
    refetch: refetchBooking,
  } = useBooking(bookingId)
  const router = useRouter()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const { user } = useAuth()
  const { data: verificationOverview } = useVerificationOverview()
  /*
   * La licencia aprobada de QUIEN SOSTIENE EL TELEFONO, que no es lo mismo que
   * "hace falta una licencia aca".
   *
   * Al confirmar la entrega se le decia "Licencia pendiente" a cualquiera --
   * incluido el anfitrion, que NO maneja y por regla del producto no necesita
   * licencia para nada (ver `has_host_identity_for_listing`). Un aviso que le
   * reprocha a alguien un requisito que no le corresponde, justo en el unico
   * momento en que no puede hacer nada al respecto.
   *
   * Solo aplica si quien esta operando la pantalla ES el huesped.
   */
  const licenseApproved = verificationOverview?.license.status === 'approved'
  const soyElHuesped = Boolean(user?.id) && booking?.guestId === user?.id
  const etapa = etapaDeInspeccion(booking?.status, 'checkin')

  const {
    mutateAsync: createInspection,
    isPending,
    photoProgress,
    totalPhotos,
  } = useCreateInspection()

  /*
   * Los errores de esta entrega son persistentes a propósito: alguien debe
   * poder leer un fallo de evidencia antes de que desaparezca. Pero pertenecen
   * a ESTE intento, no a la siguiente pantalla. Guardar su id permite retirar
   * únicamente ese error si el reintento se completa, sin borrar un aviso
   * legítimo originado en otra parte de la app.
   */
  const handoverErrorToastIdRef = useRef<string | null>(null)
  const dismissHandoverError = useCallback(() => {
    if (!handoverErrorToastIdRef.current) return
    toast.dismiss(handoverErrorToastIdRef.current)
    handoverErrorToastIdRef.current = null
  }, [])
  const showHandoverError = useCallback(
    (message: string, description?: string) => {
      dismissHandoverError()
      handoverErrorToastIdRef.current = toast.error(
        message,
        description ? { description } : undefined,
      )
    },
    [dismissHandoverError],
  )

  useEffect(() => () => dismissHandoverError(), [dismissHandoverError])

  // ─── Form State ─────────────────────────────────────────────────────────
  const [fuelLevel, setFuelLevel] = useState(50)
  /*
   * Nadie afirmo el combustible hasta que alguien mueve el control. El 50%
   * inicial es una posicion del slider, no una lectura del tablero, y guardarlo
   * como si lo fuera convierte la devolucion en una discusion sobre un numero
   * que nadie dijo.
   */
  const [fuelTouched, setFuelTouched] = useState(false)
  const [odometerText, setOdometerText] = useState('')
  const odometerInputRef = useRef<RNTextInput>(null)
  const [exteriorCondition, setExteriorCondition] = useState<ConditionGrade | undefined>(undefined)
  const [interiorCondition, setInteriorCondition] = useState<ConditionGrade | undefined>(undefined)
  const [notes, setNotes] = useState('')
  const [photos, setPhotos] = useState<InspectionPhoto[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  /*
   * Las dos firmas de la entrega.
   *
   * Una entrega de carro es un acto entre dos personas y hasta hoy solo la
   * registraba una: quien sostenía el teléfono. Las columnas existían desde el
   * principio con cero filas escritas.
   *
   * Ambas en el mismo dispositivo porque así ocurre el hecho: anfitrión y
   * huésped están parados junto al carro. Pedir dos sesiones sería inventar una
   * ceremonia que la situación real no tiene.
   */
  const [guestSignature, setGuestSignature] = useState<string | null>(null)
  const [hostSignature, setHostSignature] = useState<string | null>(null)

  /*
   * EL BORRADOR EN DISCO. Salir de esta pantalla ya no pierde nada.
   *
   * Todo lo de arriba vivia solo en memoria: quince fotos, dos firmas, el
   * odometro y el combustible. Si Android mataba la app --y quince fotos a
   * resolucion de camara son justo la carga que lo provoca-- o si alguien hacia
   * el gesto de atras sin querer, se perdia entero, con el anfitrion y el
   * huesped parados junto al carro.
   *
   * La respuesta correcta no es un dialogo de "seguro que querer salir": es que
   * salir no cueste nada. Se rehidrata una vez al montar y se guarda en cada
   * cambio; el borrador se borra al ENVIAR con exito, no al salir.
   */
  const { getDraft, saveDraft, clearDraft } = useCheckinDraftStore()
  const [hydratedBookingId, setHydratedBookingId] = useState<string | null>(null)
  const checkinStartedRef = useRef(false)

  useEffect(() => {
    if (!booking?.id || checkinStartedRef.current) return
    checkinStartedRef.current = true
    productTelemetryService.recordEvent('booking_step', RETENTION_EVENTS.checkinStarted, {
      elementId: 'checkin.photos',
      metadata: { inspectionType: 'pickup' },
    })
  }, [booking?.id])

  useEffect(() => {
    if (!bookingId || hydratedBookingId === bookingId) return

    const borrador = hydrateInspectionDraft(getDraft(bookingId, 'checkin'))
    setFuelLevel(borrador.fuelLevel)
    setFuelTouched(borrador.fuelTouched)
    setOdometerText(borrador.odometerText)
    setExteriorCondition(borrador.exteriorCondition)
    setInteriorCondition(borrador.interiorCondition)
    setNotes(borrador.notes)
    setPhotos(borrador.photos)
    setGuestSignature(borrador.guestSignature)
    setHostSignature(borrador.hostSignature)
    setErrors({})
    setHydratedBookingId(bookingId)
  }, [bookingId, getDraft, hydratedBookingId])

  useEffect(() => {
    // A route-param change reuses this component. Do not let the persist
    // effect from the previous render write its state to the new rental.
    if (!canPersistInspectionDraft(bookingId, hydratedBookingId)) return
    saveDraft(bookingId, 'checkin', {
      fuelLevel,
      fuelTouched,
      odometerText,
      exteriorCondition,
      interiorCondition,
      notes,
      photos,
      guestSignature,
      hostSignature,
    })
  }, [
    bookingId,
    exteriorCondition,
    fuelLevel,
    fuelTouched,
    guestSignature,
    hydratedBookingId,
    hostSignature,
    interiorCondition,
    notes,
    odometerText,
    photos,
    saveDraft,
  ])

  const odometerKm = odometerText ? parseInt(odometerText, 10) : 0
  /*
   * La licencia comparte el arreglo `photos` --misma camara, misma subida-- pero
   * NO es una foto del carro. Si se contara aqui, se podria enviar la entrega
   * con 14 angulos del vehiculo y la licencia haciendo de decimoquinto. Se
   * separan las dos cuentas.
   */
  const carPhotos = photos.filter((p) => p.area !== LICENSE_PHOTO_AREA)
  /*
   * Lo que decide es el CONJUNTO de areas, no cuantas fotos hay.
   *
   * Con quince obligatorias de quince areas, contar y tener eran lo mismo. Al
   * bajar a seis dejaron de serlo: contar sola aceptaria seis fotos de asientos
   * y cero de la carroceria. La cuenta sigue viva solo para la barra.
   */
  const areasObligatoriasFaltantes = faltanAreasObligatorias(carPhotos.map((foto) => foto.area))
  const hasEnoughPhotos = areasObligatoriasFaltantes.length === 0
  const hasLicensePhoto = photos.some((p) => p.area === LICENSE_PHOTO_AREA)
  const requiresLicenseEvidence = requiresLiveLicenseEvidence(soyElHuesped)
  const hasRequiredLicenseEvidence = hasRequiredLiveLicenseEvidence(soyElHuesped, hasLicensePhoto)
  const isFormValid =
    fuelTouched &&
    exteriorCondition !== undefined &&
    interiorCondition !== undefined &&
    odometerText.length > 0 &&
    hasEnoughPhotos &&
    hasRequiredLicenseEvidence &&
    guestSignature !== null &&
    hostSignature !== null
  const readinessItems = [
    {
      key: 'fuel',
      label: t('checkin.fuel_level'),
      complete: fuelTouched,
      detail: fuelTouched ? `${fuelLevel}%` : undefined,
    },
    {
      key: 'odometer',
      label: t('checkin.odometer'),
      complete: odometerText.length > 0,
    },
    {
      key: 'exterior',
      label: t('checkin.exterior_condition'),
      complete: exteriorCondition !== undefined,
    },
    {
      key: 'interior',
      label: t('checkin.interior_condition'),
      complete: interiorCondition !== undefined,
    },
    {
      key: 'photos',
      label: t('checkin.photos_title'),
      complete: hasEnoughPhotos,
      detail: t('checkin.photos_progress', {
        count: MIN_INSPECTION_PHOTOS - areasObligatoriasFaltantes.length,
        total: MIN_INSPECTION_PHOTOS,
      }),
    },
    ...(requiresLicenseEvidence
      ? [
          {
            key: 'license',
            label: t('checkin.license_title'),
            complete: hasLicensePhoto,
          },
        ]
      : []),

    {
      key: 'signatures',
      label: t('inspection.signatures_title'),
      complete: guestSignature !== null && hostSignature !== null,
    },
  ]

  // ─── Handlers ───────────────────────────────────────────────────────────

  /**
   * Marcar o desmarcar que una foto muestra un daño.
   *
   * Se alterna sobre la foto ya tomada en vez de preguntarlo aparte: la
   * afirmación y su evidencia son la misma cosa, y separarlas obliga a describir
   * con palabras lo que la foto ya muestra.
   */
  const handleToggleDamage = useCallback((area: InspectionPhotoArea) => {
    setPhotos((prev) => prev.map((p) => (p.area === area ? { ...p, isDamage: !p.isDamage } : p)))
  }, [])

  const handlePhotoTaken = useCallback((photo: InspectionPhoto) => {
    setPhotos((prev) => {
      /*
       * REPETIR UNA FOTO NO BORRA LO QUE SE AFIRMO SOBRE ELLA.
       *
       * Antes se descartaba la foto vieja entera y se metia la nueva, asi que
       * la marca de dano se perdia en silencio. Y repetir es justo lo que hace
       * alguien que quiere que el golpe se vea MEJOR: marca el dano, la foto le
       * queda oscura, la vuelve a tomar, y la marca desaparece sin decir nada.
       * El resultado es evidencia de un dano que el sistema cree que nadie
       * reporto.
       *
       * La foto cambia; la afirmacion sobre esa parte del carro se conserva
       * salvo que la nueva captura traiga la suya.
       */
      const anterior = prev.find((p) => p.area === photo.area)
      const resto = prev.filter((p) => p.area !== photo.area)
      return [...resto, { ...photo, isDamage: photo.isDamage ?? anterior?.isDamage }]
    })
  }, [])

  const handleSubmit = async () => {
    /*
     * NUNCA TERMINAR EN SILENCIO. Esto decia `if (!user || !bookingId) return`:
     * un boton habilitado que al tocarlo no hacia absolutamente nada visible.
     * Si falta la sesion es un hecho que hay que decir, no un caso que ignorar.
     */
    if (!bookingId) return
    if (!user) {
      void notification(NotificationType.Error)
      showHandoverError(t('inspection.session_needed_title'), t('inspection.session_needed_body'))
      return
    }
    // Solo a quien maneja. El anfitrion no necesita licencia y decirle
    // "Licencia pendiente" al confirmar la entrega es reprocharle un requisito
    // que no le toca, en el peor momento.
    if (soyElHuesped && !licenseApproved) {
      void notification(NotificationType.Warning)
      toast.info(t('checkin.license_required_title'), {
        description: t('checkin.license_required_body'),
      })
    }

    const formData = {
      fuelLevel,
      odometerKm,
      exteriorCondition: exteriorCondition!,
      interiorCondition: interiorCondition!,
      notes,
      photos,
      guestSignatureSvg: guestSignature,
      hostSignatureSvg: hostSignature,
    }

    // Validate
    const result = inspectionFormSchema.safeParse(formData)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const err of result.error.issues) {
        const key = err.path[0]
        if (typeof key === 'string') fieldErrors[key] = err.message
      }
      setErrors(fieldErrors)
      void notification(NotificationType.Error)
      /*
       * Y SE DICE. Antes esto solo llenaba `errors` y volvia: las llaves que
       * tienen un campo donde pintarse (odometro, notas) se veian, pero
       * `photos` --que es la que mas falla-- no tenia donde, asi que el boton
       * se veia habilitado, la persona lo tocaba, y no pasaba NADA. Un error
       * que no se muestra es identico a un boton roto.
       */
      const primera = result.error.issues[0]
      showHandoverError(
        t('checkin.error_incomplete_title'),
        primera ? t(primera.message) : t('checkin.error_incomplete_body'),
      )
      return
    }

    setErrors({})

    try {
      await createInspection({
        rentalId: bookingId,
        userId: user.id,
        type: 'checkin',
        formData,
      })

      // El borrador se descarta ACA y en ningun otro lado: ya esta en el
      // servidor, asi que conservarlo solo puede resucitar datos viejos.
      clearDraft(bookingId, 'checkin')
      productTelemetryService.recordEvent('booking_step', RETENTION_EVENTS.checkinCompleted, {
        elementId: 'checkin.submit',
        metadata: { inspectionType: 'pickup', photoCount: photos.length },
      })
      void notification(NotificationType.Success)
      dismissHandoverError()
      toast.success(t('checkin.success_title'), {
        description: t('checkin.success_desc'),
      })
      router.replace(`/trips/${bookingId}` as never)
    } catch (err) {
      // Surface the real ServiceError instead of a generic "checkin.error_failed" toast with no
      // signal of the underlying reason. Surface real ServiceError reason
      // when present (RLS denial, photo-upload failure, etc.) and capture
      // to Sentry.
      void notification(NotificationType.Error)
      // Si fallaron fotos concretas, se nombran. Si no, el motivo real.
      showHandoverError(
        mensajeDeFotosFallidas(err, t) ?? errorMessage(err, t('checkin.error_failed')),
      )
      captureError(err, { screen: 'checkin', action: 'create-inspection' })
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  /*
   * NO SE PIDEN 15 FOTOS PARA UNA RESERVA QUE NO EXISTE.
   *
   * Esta pantalla tomaba el `bookingId` de la URL y dibujaba el formulario
   * ENTERO sin preguntar por la reserva: nivel de combustible, kilometraje,
   * estado exterior e interior, quince fotos en vivo y una firma. Recien al
   * enviar el servidor decia que no. Con una reserva ajena o borrada eso es
   * pedirle a alguien diez minutos de trabajo para tirarlos.
   *
   * FALLA ABIERTO: mientras la consulta no resuelva no se muestra el muro. A
   * quien SI tiene la reserva no se le corta la inspeccion porque su red este
   * lenta -- y para eso existe la rama de carga de abajo.
   */
  /*
   * TRES HECHOS DISTINTOS, NO DOS.
   *
   * Mi primera version decia `if (!bookingLoading && (bookingError || !booking))`
   * y pintaba "Esta reserva ya no esta disponible". Eso FALLA CERRADO, que es lo
   * contrario de lo que este repo exige -- y en la peor pantalla posible: la
   * inspeccion se hace en la calle, junto al carro, con dos barras de senal.
   * `useBooking` tiene `retry: false`, y `getBookingDetail` lanza IGUAL por
   * reserva inexistente, por RLS y por un timeout de transporte. O sea que un
   * paquete perdido le decia a un anfitrion legitimo que la reserva "puede que
   * se haya cancelado, vencido o que no sea tuya", sin boton de reintentar, y su
   * unica salida lo sacaba de la entrega.
   *
   * Es el mismo defecto que `explore/[vehicleId].tsx` ya habia arreglado --"un
   * fallo de transporte y un carro que de verdad no esta son hechos distintos"--
   * reintroducido por mi. Ahora se separan:
   *
   *   1. HAY RESERVA EN CACHE -> nunca se levanta un muro, aunque `bookingError`
   *      este puesto. Un refetch de fondo que falla no puede borrar una fila que
   *      ya tenemos ni tapar una inspeccion a medio llenar.
   *   2. FALLO DE TRANSPORTE y sin cache -> se dice que no se pudo cargar y se
   *      ofrece REINTENTAR. No se afirma nada sobre la reserva.
   *   3. EL SERVIDOR CONTESTO Y NO HAY RESERVA -> ahi si, ya no esta disponible.
   */
  if (!bookingLoading && !booking && bookingError && !esReservaInexistente(bookingError)) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.background }}>
        <ScreenHeader
          title={t('checkin.title')}
          backTestID="checkin.back"
          style={{ paddingTop: insets.top + SPACING.sm }}
        />
        <View style={{ flex: 1, justifyContent: 'center', paddingHorizontal: SPACING.lg }}>
          <EmptyState
            testID="checkin.not-available-unreachable"
            unavailable
            title={t('common.error_title')}
            description={t('common.error_description')}
            action={{ label: t('common.retry'), onPress: () => void refetchBooking() }}
            secondaryAction={{
              label: t('inspection.not_available_action'),
              onPress: () => router.replace('/(tabs)/trips' as never),
            }}
          />
        </View>
      </View>
    )
  }

  /*
   * NI 15 FOTOS NI DOS FIRMAS SOBRE UNA RENTA QUE NO LAS ADMITE.
   *
   * Esta pantalla no miraba el estado de la reserva: dejaba llenar la entrega
   * entera sobre una renta cancelada, sobre una que ya se entrego, o sobre una
   * que ni siquiera se pago. La regla vive en `lib/utils/inspection-stage.ts`
   * --una sola, compartida con la devolucion-- y FALLA ABIERTO: solo detiene
   * sobre estados conocidos que hacen la entrega imposible o inutil.
   *
   * Va DESPUES de las ramas de carga y de reserva inexistente, para que un
   * `booking` que todavia no llego nunca levante este muro.
   */
  if (booking && etapa !== 'permitida') {
    const irADevolucion = etapa === 'ya_ocurrio'
    return (
      <View style={{ flex: 1, backgroundColor: colors.background }}>
        <ScreenHeader
          title={t('checkin.title')}
          backTestID="checkin.back"
          style={{ paddingTop: insets.top + SPACING.sm }}
        />
        <View style={{ flex: 1, justifyContent: 'center', paddingHorizontal: SPACING.lg }}>
          <EmptyState
            testID={`checkin.stage-${etapa}`}
            unavailable={etapa === 'cancelada' || etapa === 'terminada'}
            title={t(tituloDeEtapa(etapa, 'checkin'))}
            description={t(cuerpoDeEtapa(etapa, 'checkin'))}
            action={{
              label: irADevolucion
                ? t('inspection.stage.go_to_checkout')
                : t('inspection.stage.go_to_trip'),
              onPress: () =>
                router.replace(
                  (irADevolucion
                    ? `/checkin/checkout/${bookingId}`
                    : `/trips/${bookingId}`) as never,
                ),
            }}
          />
        </View>
      </View>
    )
  }

  if (!bookingLoading && !booking) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.background }}>
        <ScreenHeader
          title={t('checkin.title')}
          backTestID="checkin.back"
          style={{ paddingTop: insets.top + SPACING.sm }}
        />
        <View style={{ flex: 1, justifyContent: 'center', paddingHorizontal: SPACING.lg }}>
          <EmptyState
            testID="checkin.not-available"
            unavailable
            title={t('inspection.not_available_title')}
            description={t('inspection.not_available_body')}
            action={{
              label: t('inspection.not_available_action'),
              onPress: () => router.replace('/(tabs)/trips' as never),
            }}
          />
        </View>
      </View>
    )
  }

  return (
    <View style={{ flex: 1, backgroundColor: colors.background }}>
      <ScreenHeader
        title={t('checkin.title')}
        subtitle={t('checkin.subtitle')}
        backTestID="checkin.back"
        style={{ paddingTop: insets.top + SPACING.sm }}
      />

      {/* Form */}
      <KeyboardAwareView
        includeTopInset={false}
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingHorizontal: SPACING.md,
          paddingBottom: insets.bottom + SPACING['2xl'] + 80,
          gap: SPACING.lg,
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {/* Fuel Level */}
        <View style={{ gap: SPACING.xs }}>
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 16, color: colors.textPrimary }}>
            {t('checkin.fuel_level')}
          </Text>
          <FuelLevelSlider
            value={fuelLevel}
            onChange={(v) => {
              setFuelTouched(true)
              setFuelLevel(v)
            }}
          />
          {!fuelTouched && (
            <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textSecondary }}>
              {t('checkin.fuel_confirm_hint')}
            </Text>
          )}
        </View>

        {/* Odometer */}
        <TextInput
          label={t('checkin.odometer')}
          placeholder={t('checkin.odometer_placeholder')}
          keyboardType="number-pad"
          value={odometerText}
          ref={odometerInputRef}
          onChangeText={(text) =>
            setOdometerText(sanitizeOdometerText(text, odometerInputRef.current))
          }
          error={errors.odometerKm ? t(errors.odometerKm) : undefined}
          testID="checkin.odometer-input"
        />

        {/* Exterior Condition */}
        <ConditionPicker
          label={t('checkin.exterior_condition')}
          value={exteriorCondition}
          onChange={setExteriorCondition}
        />

        {/* Interior Condition */}
        <ConditionPicker
          label={t('checkin.interior_condition')}
          value={interiorCondition}
          onChange={setInteriorCondition}
        />

        {/* Photos */}
        <View style={{ gap: SPACING.sm }}>
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 16, color: colors.textPrimary }}>
            {t('checkin.photos_title')}
          </Text>
          <Text style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textSecondary }}>
            {t('checkin.photos_subtitle')}
          </Text>
          {!hasEnoughPhotos && (
            <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.warning }}>
              {t('checkin.missing_required_photos', {
                areas: areasObligatoriasFaltantes
                  .map((area: InspectionPhotoArea) => t(`checkin.photo_${area}`))
                  .join(', '),
              })}
            </Text>
          )}
          {/* El error del esquema sobre las fotos necesitaba un lugar donde
              existir. Sin esto se guardaba en `errors.photos` y no lo veia nadie. */}
          {errors.photos ? (
            <Text
              style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.error }}
              testID="checkin.photos-error"
            >
              {t(errors.photos)}
            </Text>
          ) : null}
          <InspectionPhotoGrid
            photos={photos}
            onPhotoTaken={handlePhotoTaken}
            onToggleDamage={handleToggleDamage}
            areas={INSPECTION_PHOTO_AREAS}
          />
        </View>

        {/* LA LICENCIA, EN VIVO, ANTES DE ENTREGAR LAS LLAVES.
            No verifica nada ni llama a ningun proveedor: eso ya paso una sola
            vez en el onboarding contra SERTRACEN. Esto deja CONSTANCIA de que el
            huesped traia su licencia encima hoy, que es lo que una licencia
            aprobada hace tres meses no prueba.
            Sin espera, sin red, sin poder fallar: se toma la foto y se sigue.
            Reusa la misma cuadricula --y con ella la camara en vivo, el
            almacenamiento durable y la subida-- con un area sola y SIN marcado
            de danio, que sobre el documento de una persona no tendria sentido. */}
        {requiresLicenseEvidence ? (
          <View style={{ gap: SPACING.md }}>
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 17, color: colors.textPrimary }}>
              {t('checkin.license_title')}
            </Text>
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 14,
                lineHeight: 20,
                color: colors.textSecondary,
                marginTop: -SPACING.xs,
              }}
            >
              {t('checkin.license_subtitle')}
            </Text>
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 13,
                lineHeight: 18,
                color: colors.textAccent,
              }}
              testID="checkin.license-required-hint"
            >
              {t('checkin.license_required_for_checkin')}
            </Text>
            <InspectionPhotoGrid
              photos={photos}
              onPhotoTaken={handlePhotoTaken}
              areas={[LICENSE_PHOTO_AREA]}
              testIDPrefix="checkin.license"
            />
          </View>
        ) : null}

        {/* Firmas — el acto que faltaba.
            Van DESPUÉS de las fotos y las condiciones a propósito: se firma lo
            que ya se revisó, no un formulario en blanco. */}
        <View style={{ gap: SPACING.md }}>
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 17,
              color: colors.textPrimary,
            }}
          >
            {t('inspection.signatures_title')}
          </Text>
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 14,
              lineHeight: 20,
              color: colors.textSecondary,
              marginTop: -SPACING.xs,
            }}
          >
            {t('inspection.signatures_help')}
          </Text>

          {/* `value` no es decoracion: sin el, un borrador recuperado dejaba
              los dos recuadros EN BLANCO mientras la tarjeta decia "Firmas OK",
              y tocarlos borraba la firma que si estaba guardada. */}
          <HandoverPresenceCard
            rentalId={booking?.id ?? bookingId}
            inspectionType="pickup"
            isGuest={soyElHuesped}
          />
          <SignaturePad
            label={t('inspection.signature_host')}
            value={hostSignature}
            onChange={setHostSignature}
            testID="checkin.signature-host"
          />
          <SignaturePad
            label={t('inspection.signature_guest')}
            value={guestSignature}
            onChange={setGuestSignature}
            testID="checkin.signature-guest"
          />
        </View>

        {/* Notes */}
        <TextInput
          label={t('checkin.notes')}
          placeholder={t('checkin.notes_placeholder')}
          multiline
          numberOfLines={4}
          value={notes}
          onChangeText={(text) => setNotes(text.slice(0, 500))}
          error={errors.notes ? t(errors.notes) : undefined}
          testID="checkin.notes-input"
          style={{ minHeight: 100, textAlignVertical: 'top' }}
        />

        <InspectionReadinessCard
          title={isFormValid ? t('checkin.ready_title') : t('checkin.readiness_title')}
          items={readinessItems}
          testID="checkin.readiness"
        />
      </KeyboardAwareView>

      {/* Bottom CTA */}
      <View
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          paddingHorizontal: SPACING.md,
          paddingBottom: insets.bottom + SPACING.md,
          paddingTop: SPACING.md,
          backgroundColor: colors.background,
          borderTopWidth: 1,
          borderTopColor: colors.border,
        }}
      >
        {/*
          EL PROGRESO VIVE ACA, no dentro del scroll.
          Subir dieciseis fotos desde datos moviles toma bastante, y el aviso
          estaba al final de un formulario largo: en cuanto empezaba la subida
          el CTA se volvia un spinner sin numero y el unico indicador quedaba
          fuera de la pantalla. Una espera sin cifra se siente como una app
          colgada, y de ahi salen los toques repetidos sobre una accion
          irreversible.
        */}
        {isPending && totalPhotos > 0 ? (
          <View style={{ gap: SPACING.xs, marginBottom: SPACING.sm }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
              <Text
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: colors.textSecondary,
                }}
                numberOfLines={1}
              >
                {t('checkin.uploading_photos')}
              </Text>
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 13, color: colors.textAccent }}>
                {`${photoProgress}/${totalPhotos}`}
              </Text>
            </View>
            <View
              style={{
                height: 6,
                borderRadius: RADIUS.full,
                backgroundColor: colors.surfaceSubtle,
                overflow: 'hidden',
              }}
              accessibilityRole="progressbar"
              accessibilityLabel={t('checkin.uploading_photos')}
              accessibilityValue={{ min: 0, max: totalPhotos, now: photoProgress }}
              testID="checkin.upload-progress"
            >
              <View
                style={{
                  height: '100%',
                  width: `${Math.round((photoProgress / Math.max(totalPhotos, 1)) * 100)}%`,
                  backgroundColor: colors.textAccent,
                }}
              />
            </View>
          </View>
        ) : null}
        <Button
          title={t('checkin.confirm_checkin')}
          onPress={() => void handleSubmit()}
          loading={isPending}
          disabled={!isFormValid || isPending}
          testID="checkin.confirm-button"
        />
      </View>
    </View>
  )
}
